import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowFwdIcon from "@mui/icons-material/ArrowForwardRounded";
import { Alert, AlertTitle, Box, Button, Stack, Step, StepLabel, Stepper } from "@mui/material";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { SubmissionAttempt } from "@entrilia/shared/utilities/submissionAttempt";
import { resolveSubmissionAttempt } from "@entrilia/shared/utilities/submissionAttempt";
import { api } from "../../../../../api/client";
import useClientContext from "../../../../../contexts/useClientContext";
import useClientPageTitle from "../../../../../contexts/useClientPageTitle";
import useImpersonationBlockContext from "../../../../../contexts/useImpersonationBlockContext";
import { pageRoutes } from "../../../../../routes";
import { workspaceInvestorSelector } from "../../../../../store/state/user/selectors";
import LeavePageConfirmationDialog from "../../common/LeavePageConfirmationDialog";
import SettingsWrapper from "../../SettingsWrapper";
import { addNewContactsToStoredChanges } from "../contactChangesStorage";
import { CHANGE_REQUEST_SUBMITTED_MESSAGE, resolveDisplayedComment } from "../membersChangeRequestHelper";
import ContactDetailsForm from "./ContactDetailsForm";
import FundAccessForm from "./FundAccessForm";
import NewContactReview from "./NewContactReview";
import {
  getInitialNewContactState,
  getManageableFunds,
  newContactStepLabels,
  newContactSteps,
  nextStepAction,
  previousStepAction,
  stateToNewContactChanges,
  stateToUpsertMembersChangeRequestRequest,
} from "./newContactState";
import PermissionsForm from "./PermissionsForm";

const upsertMembersChangeRequest = withErrorHandling(api.membersChangeRequests.upsert);

const NewContactPage = () => {
  useClientPageTitle("New Contact");

  const { clientCode } = useClientContext();
  const workspaceInvestor = useSelector(workspaceInvestorSelector);
  const navigate = useNavigate();

  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { isBlockedUnderImpersonation, showImpersonationBlockedDialog } = useImpersonationBlockContext();

  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [state, setState] = useState(getInitialNewContactState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionAttempt, setSubmissionAttempt] = useState<SubmissionAttempt>();

  const investorId = workspaceInvestor?.investorId ?? "";
  const getInvestorContacts = useCallback(() => api.investorContacts.getInvestorContacts(investorId), [investorId]);
  const [contactsResponse, fetchContactsError] = useFetch(getInvestorContacts, { disabled: !investorId });

  const getChangeRequestStatusView = useCallback(
    () => api.membersChangeRequests.getStatusView(investorId),
    [investorId]
  );
  const [latestChangeRequest, fetchChangeRequestError] = useFetch(getChangeRequestStatusView, {
    disabled: !investorId,
  });

  if (fetchContactsError || fetchChangeRequestError) {
    logError(fetchContactsError || fetchChangeRequestError, "[NewContactPage]");
    return <DataLoadingFailed title="Failed to load contacts" />;
  }

  if (contactsResponse === undefined || latestChangeRequest === undefined) {
    return <InlineLoader />;
  }

  const manageableFunds = getManageableFunds(contactsResponse.contacts);

  const navigateToContactsPage = () => navigate(`/${clientCode}/${pageRoutes.settings}/lp/members`);

  if (manageableFunds.length === 0) {
    return (
      <Alert severity="warning">
        <AlertTitle>No funds available for adding contacts</AlertTitle>
        <Button variant="text" onClick={navigateToContactsPage}>
          Go Back
        </Button>
      </Alert>
    );
  }

  const handleBackButtonClick = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmLeave = () => {
    setShowCancelConfirmation(false);
    navigateToContactsPage();
  };

  const handleCancelLeave = () => {
    setShowCancelConfirmation(false);
  };

  const handlePreviousStep = () => {
    setState(previousStepAction());
  };

  const handleNextStep = async () => {
    if (state.step !== "review") {
      setState(nextStepAction());
      return;
    }

    if (!workspaceInvestor) {
      return;
    }

    setIsSubmitting(true);
    // Signature covers the submitted content and base etag - never the wizard's own step - so a retry reuses this id and an edit gets a new one
    const attempt = resolveSubmissionAttempt(
      [
        state.contacts,
        state.selectedFundIds,
        state.role,
        state.selectedCategoryExternalIds,
        state.comment,
        latestChangeRequest?.etag,
      ],
      submissionAttempt
    );
    setSubmissionAttempt(attempt);
    const payload = stateToUpsertMembersChangeRequestRequest(
      state,
      workspaceInvestor.fundInvestors,
      latestChangeRequest,
      attempt.id
    );
    const [, error] = await upsertMembersChangeRequest(workspaceInvestor.investorId, payload);
    setIsSubmitting(false);
    if (error) {
      if (isBlockedUnderImpersonation(error)) {
        showImpersonationBlockedDialog();
        return;
      }
      logError(error, "[NewContactPage] upsertMembersChangeRequest");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(CHANGE_REQUEST_SUBMITTED_MESSAGE);
    navigateToContactsPage();
  };

  const handleAddAndContinue = () => {
    if (!workspaceInvestor) {
      return;
    }

    addNewContactsToStoredChanges(
      workspaceInvestor.investorId,
      stateToNewContactChanges(state, workspaceInvestor.fundInvestors),
      state.comment
    );
    navigateToContactsPage();
  };

  const stepIndex = newContactSteps.indexOf(state.step);
  const nextStep = newContactSteps[stepIndex + 1];
  const submitLabel = latestChangeRequest.changeRequestId ? "Update Request" : "Submit Request";
  const nextStepLabel = nextStep === undefined ? submitLabel : newContactStepLabels[nextStep];

  return (
    <SettingsWrapper
      title="New Contact"
      subtitle={workspaceInvestor?.investorTitle}
      scrollOwner="wrapper"
      onBackButtonClick={handleBackButtonClick}
      Subheader={
        <Box sx={{ py: 1.5, px: 3, display: "flex", justifyContent: "center" }}>
          <Stepper activeStep={stepIndex}>
            {newContactSteps.map((step) => (
              <Step key={step}>
                <StepLabel>{newContactStepLabels[step]}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      }
      Footer={
        <Box sx={{ py: 2, px: 3, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={handlePreviousStep}
            sx={{ visibility: state.step === "contactDetails" ? "hidden" : "visible" }}
          >
            Previous
          </Button>
          <Stack direction="row" spacing={1}>
            {state.step === "review" && (
              <Button variant="outlined" disabled={isSubmitting} onClick={handleAddAndContinue}>
                Add &amp; Continue Editing
              </Button>
            )}
            <Button
              loading={isSubmitting}
              variant="contained"
              endIcon={<ArrowFwdIcon />}
              onClick={handleNextStep}
              disabled={state.isNextStepDisabled}
            >
              {nextStepLabel}
            </Button>
          </Stack>
        </Box>
      }
    >
      {state.step === "contactDetails" && <ContactDetailsForm state={state} setState={setState} />}
      {state.step === "fundAccess" && <FundAccessForm state={state} setState={setState} funds={manageableFunds} />}
      {state.step === "permissions" && <PermissionsForm state={state} setState={setState} />}
      {state.step === "review" && (
        <NewContactReview
          state={state}
          setState={setState}
          funds={manageableFunds}
          comment={resolveDisplayedComment(state.comment, latestChangeRequest) ?? ""}
        />
      )}
      <LeavePageConfirmationDialog
        open={showCancelConfirmation}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </SettingsWrapper>
  );
};

export default NewContactPage;
